import {
    AppBar,
    Toolbar,
    Stack,
    List,
    Tab,
    Tabs,
    IconButton,
    useMediaQuery,
    Drawer,
    Divider,
    Avatar,
    Menu,
    MenuItem,
    TabProps,
    useTheme,
} from '@mui/material'
import pxToRem from '../../helpers/pxToRem'
import styles from './DashboardHeader.module.scss'
import sparkIcon from '../../assets/images/header/Spark Relief Teachers Logo.png'
import { useLocation, useNavigate } from 'react-router-dom'
import a11yProps from '../../helpers/a11yProps'
import MenuIcon from '@mui/icons-material/Menu'
import { useEffect, useMemo, useState } from 'react'
import { DashboardTabNames, TabNames } from '../../routers/type'
import { DrawerIcon } from './helper'
import { useSelector } from 'react-redux'
import { RootState } from '../../redux/store'

import HomeIcon from '@mui/icons-material/Home'
import LogoutIcon from '@mui/icons-material/Logout'
import SettingsIcon from '@mui/icons-material/Settings'
import HelpCenterIcon from '@mui/icons-material/HelpCenter'
import sparkLogo from '../../assets/images/share/sparkIcon.png'
import useSignOutUser from '../../hooks/useSignOutUser'
import { tabNames } from '../../routers/helpers'
import { canAccessMySchool } from '../../middleware/school'
import {
    selectSchoolState,
    selectSchoolUserAuthority,
    selectSchoolUserState,
} from '../../redux/schoolSlice'
import HideOnScroll from '../HideOnScroll'

const getMenuItems = (schoolId: string) => [
    {
        label: 'Home',
        name: '',
        otherKeys: ['bookings'],
        link: `${tabNames.app.school.index}/${schoolId}`,
        icon: <HomeIcon />,
    },
    {
        label: 'My School',
        name: 'my-school',
        otherKeys: ['my-school'],
        link: `${tabNames.app.school.index}/${schoolId}/${tabNames.app.school.mySchool.index}`,
    },
    {
        label: 'Help Desk',
        name: 'help',

        link: `/${TabNames.HELP_DESK}`,
        icon: <HelpCenterIcon />,
    },
    {
        label: 'Settings',
        name: 'settings',
        link: `${tabNames.app.school.index}/${schoolId}/${DashboardTabNames.SETTINGS}`,
        icon: <SettingsIcon />,
    },
]

const findTab = (
    items: any[],
    path: string | undefined,
    isMobile?: boolean
) => {
    let tab: number
    if (path === undefined || path === '') {
        tab = 0
    } else {
        tab = items.findIndex(
            (el) =>
                el.link.substring(el.link.lastIndexOf('/') + 1) === path ||
                (el.otherKeys && el.otherKeys.includes(path))
        )
    }

    if (isMobile) {
        return tab
    }
    return tab > 2 ? -1 : tab
}

const SchoolHeader = ({ schoolUid }: { schoolUid?: string }) => {
    const navigate = useNavigate()
    const location = useLocation()

    const isUserData = useSelector((state: RootState) => !!state.user.user.data)
    const profilePictureURL = useSelector(
        (state: RootState) => state.user.user.data?.profilePictureURL
    )
    const MENU_ITEMS = useMemo(
        () => (schoolUid ? getMenuItems(schoolUid) : []),
        [schoolUid]
    )

    const path = location.pathname.split('/').at(4)

    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

    const [drawerOpen, setDrawerOpen] = useState(false)

    const [selectedTab, setSelectedTab] = useState(
        findTab(MENU_ITEMS, path, isMobile)
    )
    useEffect(() => {
        setSelectedTab(findTab(MENU_ITEMS, path, isMobile))
    }, [path, isMobile, MENU_ITEMS])

    const onTabChangeSchool = (_: React.SyntheticEvent, newValue: number) => {
        if (newValue >= 0 && newValue < MENU_ITEMS.length) {
            navigate(MENU_ITEMS?.[newValue].link)
        }
    }

    const [signOutUser] = useSignOutUser()
    const handleLogout = async () => signOutUser(() => navigate('/'))

    const [anchorEl, setAnchorEl] = useState<null | Element>(null)
    const handleMenu = (event: React.MouseEvent<Element>) => {
        setAnchorEl(event.currentTarget)
    }
    const handleClose = () => {
        setAnchorEl(null)
    }

    const HamburgerMenu = () => (
        <Drawer
            open={drawerOpen}
            anchor={'right'}
            onClose={() => setDrawerOpen(!drawerOpen)}
        >
            <List sx={{ paddingTop: '40px' }}>
                <Tabs
                    orientation={'vertical'}
                    value={selectedTab >= 0 ? selectedTab : false}
                    onChange={(e, newValue) => {
                        onTabChangeSchool(e, newValue)
                        setDrawerOpen(false)
                    }}
                    sx={{
                        '& .MuiTab-root.Mui-selected': {
                            color: styles.primary500,
                            fontWeight: isMobile ? 600 : 700,
                        },
                    }}
                >
                    {MENU_ITEMS.map((el, i) => {
                        if (el.name === 'my-school') {
                            return (
                                <MySchoolTab
                                    key={`school_header_${i}`}
                                    sx={{
                                        '&.MuiTab-root': {
                                            alignItems: 'flex-end',
                                        },
                                    }}
                                    aria-label={el.name}
                                    label={DrawerIcon(el.label, el.icon)}
                                    className={styles.hamburgerTab}
                                    {...a11yProps(i)}
                                />
                            )
                        } else {
                            return (
                                <Tab
                                    key={`school_header_${i}`}
                                    sx={{
                                        '&.MuiTab-root': {
                                            alignItems: 'flex-end',
                                        },
                                    }}
                                    aria-label={el.name}
                                    label={DrawerIcon(el.label, el.icon)}
                                    className={styles.hamburgerTab}
                                    {...a11yProps(i)}
                                />
                            )
                        }
                    })}
                    <Divider sx={{ width: '80%', alignSelf: 'center' }} />
                    <Tab
                        sx={{ '&.MuiTab-root': { alignItems: 'flex-end' } }}
                        aria-label="logout"
                        label={DrawerIcon('Logout', <LogoutIcon />)}
                        className={styles.hamburgerTab}
                        onClick={handleLogout}
                    />
                </Tabs>
            </List>
        </Drawer>
    )

    const userAccountState = useSelector(selectSchoolUserState)
    const handleTabClick = (link: string) => {
        // if (link !== location.pathname) {
        navigate(link)
        // }
    }

    return (
        <HideOnScroll>
            {/* <Box sx={{ backgroundColor: '#fff' }}> */}
            <AppBar
                // position="static"
                sx={{
                    // height: '100%',
                    px: 3,
                    py: 0,
                    backgroundColor: '#fff',
                    boxShadow: 'none',
                }}
            >
                <Toolbar variant="dense">
                    <img
                        className={styles.icon}
                        src={sparkIcon}
                        alt="Spark Icon (Text only)"
                        onClick={() => navigate(`/`)}
                    />
                    <Stack ml={'auto'}>
                        {isMobile ? (
                            <>
                                <IconButton onClick={() => setDrawerOpen(true)}>
                                    <MenuIcon />
                                </IconButton>
                                <HamburgerMenu />
                            </>
                        ) : (
                            <List sx={{ padding: 0 }}>
                                <Tabs
                                    key={`schools_tabs`}
                                    value={
                                        selectedTab >= 0 ? selectedTab : false
                                    }
                                    onChange={onTabChangeSchool}
                                    slotProps={{
                                        indicator: {
                                            sx: {
                                                backgroundColor:
                                                    styles.secondaryBlue800,
                                                height: 4,
                                                color: styles.secondaryBlue800,
                                                borderColor:
                                                    styles.secondaryBlue800,
                                            },
                                        },
                                    }}
                                    sx={{
                                        '& .MuiTabs-indicator': {
                                            // backgroundColor: styles.primaryBlue500,
                                            backgroundColor: 'none',
                                        },
                                        '& .MuiTab-root.Mui-selected': {
                                            color: styles.secondaryBlue800,
                                            border: 0,
                                            // fontWeight: isMobile ? 600 : 700
                                        },
                                        '& button': {
                                            borderRadius: 2,
                                            // margin: '6px',
                                            padding: pxToRem(12),
                                            transition: 'all 300ms',
                                        },
                                        '& button:hover': {
                                            background: 'none',
                                            color: styles.secondaryBlue300,
                                        },
                                        '& button:focus': {
                                            background: 'none',
                                        },
                                        '& button:active': {
                                            background: 'none',
                                        },
                                        '& button.Mui-selected': {
                                            background: 'none',
                                        },
                                        '& button.Mui-disabled': {
                                            color: styles.neutrals200,
                                            background: 'none',
                                        },
                                    }}
                                >
                                    <Tab
                                        key={`school_tab_0`}
                                        aria-label={MENU_ITEMS?.[0]?.name}
                                        label={MENU_ITEMS?.[0]?.label}
                                        className={styles.hamburgerTab}
                                        onClick={() => {
                                            // Handle School Approved homepage scenario
                                            handleTabClick(
                                                userAccountState === 'approved'
                                                    ? `${MENU_ITEMS?.[0]?.link}/bookings/upcoming`
                                                    : MENU_ITEMS?.[0]?.link
                                            )
                                        }}
                                        {...a11yProps(0)}
                                    />
                                    <MySchoolTab
                                        key={`school_tab_1`}
                                        aria-label={MENU_ITEMS?.[1]?.name}
                                        label={MENU_ITEMS?.[1]?.label}
                                        className={styles.hamburgerTab}
                                        onClick={() => {
                                            handleTabClick(
                                                MENU_ITEMS?.[1]?.link
                                            )
                                        }}
                                        {...a11yProps(1)}
                                    />
                                    <Tab
                                        key={`school_tab_2`}
                                        aria-label={MENU_ITEMS?.[2]?.name}
                                        label={MENU_ITEMS?.[2]?.label}
                                        className={styles.hamburgerTab}
                                        onClick={() => {
                                            handleTabClick(
                                                MENU_ITEMS?.[2]?.link
                                            )
                                        }}
                                        {...a11yProps(2)}
                                    />
                                </Tabs>
                            </List>
                        )}
                    </Stack>
                    {!isMobile && isUserData ? (
                        <div>
                            <IconButton
                                // size='medium'
                                size="small"
                                aria-label="account of current user"
                                aria-controls="menu-appbar"
                                aria-haspopup="true"
                                onClick={handleMenu}
                                color="inherit"
                                sx={{ marginLeft: pxToRem(40) }}
                            >
                                <Avatar
                                    src={profilePictureURL || sparkLogo}
                                    sx={{
                                        height: 40,
                                        width: 40,
                                    }}
                                    alt="User Photo"
                                />
                            </IconButton>
                            <Menu
                                id="menu-appbar"
                                anchorEl={anchorEl}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                keepMounted
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                open={Boolean(anchorEl)}
                                onClose={handleClose}
                                slotProps={{
                                    paper: {
                                        className: styles.dotButtonMenu,
                                    },
                                    list: {
                                        className: styles.menuListcontainer,
                                    },
                                }}
                            >
                                <MenuItem
                                    className={styles.hoverMenuItem}
                                    onClick={() => {
                                        handleClose()
                                        navigate(
                                            `${tabNames.app.school.index}/${schoolUid}/${DashboardTabNames.SETTINGS}`
                                        )
                                    }}
                                >
                                    Settings
                                </MenuItem>
                                <MenuItem
                                    className={styles.hoverMenuItem}
                                    onClick={handleLogout}
                                >
                                    Logout
                                </MenuItem>
                            </Menu>
                        </div>
                    ) : null}
                </Toolbar>
            </AppBar>
            {/* </Box> */}
        </HideOnScroll>
    )
}

const MySchoolTab = (props: TabProps) => {
    const schoolAccountState = useSelector(selectSchoolState)
    const userAccountState = useSelector(selectSchoolUserState)
    const role = useSelector(selectSchoolUserAuthority)

    const disabled =
        !schoolAccountState ||
        !userAccountState ||
        !role ||
        !canAccessMySchool(schoolAccountState, userAccountState, role)

    return <Tab {...props} disabled={disabled} />
}

export default SchoolHeader
